import { Icon } from "@iconify/react";
import { useState, useMemo } from "react";
const PaginationControls = ({ pagination, handlePageChange }) => {
  const totalPages = Math.ceil(pagination.total / pagination.perPage);
  const start = (pagination.currentPage - 1) * pagination.perPage + 1;
  const end = Math.min(
    pagination.currentPage * pagination.perPage,
    pagination.total
  );
  const [isPrevNextDisabled, setIsPrevNextDisabled] = useState(false);
  // Handle prev/next button click with 1-second disable
  const handlePrevNextClick = (page) => {
    setIsPrevNextDisabled(true);
    handlePageChange(page);
    setTimeout(() => {
      setIsPrevNextDisabled(false);
    }, 1000);
  };
  // Pagination number logic
  const pageNumbers = useMemo(() => {
    const pages = [];
    const current = pagination.currentPage;
    const addPage = (p) => {
      if (pages[pages.length - 1] !== p) {
        pages.push(p);
      }
    };
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) addPage(i);
    } else {
      if (current <= 3) {
        for (let i = 1; i <= 3; i++) addPage(i);
        pages.push("...");
        addPage(totalPages);
      } else if (current >= totalPages - 3) {
        addPage(1);
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) addPage(i);
      } else {
        addPage(1);
        pages.push("...");
        addPage(current - 1);
        addPage(current);
        addPage(current + 1);
        pages.push("...");
        addPage(totalPages);
      }
    }
    return pages;
  }, [pagination.currentPage, totalPages]);
  if (totalPages === 0) {
    return null;
  }
  return (
    <>
      <style>
        {`
          .pagination .page-link {
            transition: all 0.2s ease;
          }
          .pagination .page-item.active .page-link {
            background-color: #E63C23 !important;
            color: white !important;
          }
          .pagination .page-item.disabled .page-link {
            opacity: 0.5;
            cursor: not-allowed;
          }
          @media (max-width: 576px) {
            .pagination .page-link {
              width: 32px;
              height: 32px;
              font-size: 14px;
            }
          }
        `}
      </style>
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 mx-3 gap-3">
        <span aria-live="polite">
          Showing {start} to {end} of {pagination.total} entries
        </span>
        <nav aria-label="Pagination">
          <ul className="pagination m-0 d-flex flex-wrap gap-2">
            {/* First */}
            <li
              className={`page-item ${
                pagination.currentPage === 1 || isPrevNextDisabled
                  ? "disabled"
                  : ""
              }`}
            >
              <button
                className="page-link"
                style={buttonStyle("#e2533dff", "white")}
                onClick={() => handlePageChange(1)}
                disabled={pagination.currentPage === 1 || isPrevNextDisabled}
                aria-label="First page"
              >
                <Icon icon="ep:d-arrow-left" style={iconStyle} />
              </button>
            </li>
            {/* Prev */}
            <li
              className={`page-item ${
                pagination.currentPage === 1 || isPrevNextDisabled
                  ? "disabled"
                  : ""
              }`}
            >
              <button
                className="page-link"
                style={buttonStyle("#e2533dff", "white")}
                onClick={() => handlePrevNextClick(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1 || isPrevNextDisabled}
                aria-label="Previous page"
              >
                <Icon icon="ep:arrow-left" style={iconStyle} />
              </button>
            </li>
            {/* Pages */}
            {pageNumbers.map((page, index) => (
              <li
                key={index}
                className={`page-item ${
                  pagination.currentPage === page ? "active" : ""
                } ${page === "..." ? "disabled" : ""}`}
              >
                {page === "..." ? (
                  <span
                    className="page-link"
                    style={{
                      ...buttonStyle("#F5F5F5", "#E63C23"),
                      cursor: "default",
                    }}
                  >
                    ...
                  </span>
                ) : (
                  <button
                    className="page-link"
                    style={buttonStyle(
                      pagination.currentPage === page ? "#E63C23" : "#F5F5F5",
                      pagination.currentPage === page ? "white" : "#E63C23"
                    )}
                    onClick={() => handlePageChange(page)}
                    aria-label={`Go to page ${page}`}
                  >
                    {page}
                  </button>
                )}
              </li>
            ))}
            {/* Next */}
            <li
              className={`page-item ${
                pagination.currentPage === totalPages || isPrevNextDisabled
                  ? "disabled"
                  : ""
              }`}
            >
              <button
                className="page-link"
                style={buttonStyle("#e2533dff", "white")}
                onClick={() => handlePrevNextClick(pagination.currentPage + 1)}
                disabled={
                  pagination.currentPage === totalPages || isPrevNextDisabled
                }
                aria-label="Next page"
              >
                <Icon icon="ep:arrow-right" style={iconStyle} />
              </button>
            </li>
            {/* Last */}
            <li
              className={`page-item ${
                pagination.currentPage === totalPages || isPrevNextDisabled
                  ? "disabled"
                  : ""
              }`}
            >
              <button
                className="page-link"
                style={buttonStyle("#e2533dff", "white")}
                onClick={() => handlePageChange(totalPages)}
                disabled={
                  pagination.currentPage === totalPages || isPrevNextDisabled
                }
                aria-label="Last page"
              >
                <Icon icon="ep:d-arrow-right" style={iconStyle} />
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </>
  );
};
// Button style
const buttonStyle = (bg, color) => ({
  backgroundColor: bg,
  color: color,
  border: "none",
  borderRadius: "50%",
  width: "36px",
  height: "36px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "background-color 0.2s, transform 0.1s",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  cursor: "pointer",
});
const iconStyle = { fontSize: "20px", color: "white" };
export default PaginationControls;


















// import { Icon } from "@iconify/react";
// import { useState, useMemo, useRef, useCallback } from "react";

// const PaginationControls = ({ pagination, handlePageChange }) => {
//   const totalPages = Math.ceil(pagination.total / pagination.perPage);
//   const start = (pagination.currentPage - 1) * pagination.perPage + 1;
//   const end = Math.min(
//     pagination.currentPage * pagination.perPage,
//     pagination.total
//   );
//   const [isDisabled, setIsDisabled] = useState(false);
//   const timeoutRef = useRef(null);
//   const pendingPageRef = useRef(null);

//   // Handle any page change with debouncing
//   const handlePageChangeWithDelay = useCallback((page) => {
//     if (page === pagination.currentPage) return;
    
//     // Store the pending page
//     pendingPageRef.current = page;
    
//     // Clear existing timeout
//     if (timeoutRef.current) {
//       clearTimeout(timeoutRef.current);
//     }
    
//     // Set disabled state
//     setIsDisabled(true);
    
//     // Set new timeout
//     timeoutRef.current = setTimeout(() => {
//       if (pendingPageRef.current !== null) {
//         handlePageChange(pendingPageRef.current);
//         pendingPageRef.current = null;
//       }
      
//       // Re-enable after API call delay
//       setTimeout(() => {
//         setIsDisabled(false);
//       }, 800);
//     }, 300); // 300ms debounce delay
//   }, [pagination.currentPage, handlePageChange]);

//   // Pagination number logic - Sliding window (6 pages visible)
//   const pageNumbers = useMemo(() => {
//     const pages = [];
//     const visiblePages = 6; // Number of pages to show at once
//     const current = pagination.currentPage;

//     if (totalPages <= visiblePages) {
//       // If total pages are less than or equal to visible pages, show all
//       for (let i = 1; i <= totalPages; i++) {
//         pages.push(i);
//       }
//     } else {
//       // Sliding window logic
//       let start = Math.max(1, current - Math.floor(visiblePages / 2));
//       let end = start + visiblePages - 1;

//       // Adjust if end exceeds total pages
//       if (end > totalPages) {
//         end = totalPages;
//         start = Math.max(1, end - visiblePages + 1);
//       }

//       for (let i = start; i <= end; i++) {
//         pages.push(i);
//       }
//     }

//     return pages;
//   }, [pagination.currentPage, totalPages]);

//   if (totalPages === 0) {
//     return null;
//   }

//   return (
//     <>
//       <style>
//         {`
//           .pagination .page-link {
//             transition: all 0.2s ease;
//           }
//           .pagination .page-item.active .page-link {
//             background-color: #E63C23 !important;
//             color: white !important;
//           }
//           .pagination .page-item.disabled .page-link {
//             opacity: 0.5;
//             cursor: not-allowed;
//           }
//           @media (max-width: 576px) {
//             .pagination .page-link {
//               width: 32px;
//               height: 32px;
//               font-size: 14px;
//             }
//           }
//         `}
//       </style>
//       <div className="d-flex flex-column flex-md-row justify-content-between align-items-center mt-4 mx-3 gap-3">
//         <span aria-live="polite">
//           Showing {start} to {end} of {pagination.total} entries
//         </span>
//         <nav aria-label="Pagination">
//           <ul className="pagination m-0 d-flex flex-wrap gap-2">
//             {/* First */}
//             <li
//               className={`page-item ${
//                 pagination.currentPage === 1 || isDisabled
//                   ? "disabled"
//                   : ""
//               }`}
//             >
//               <button
//                 className="page-link"
//                 style={buttonStyle("#e2533dff", "white")}
//                 onClick={() => handlePageChangeWithDelay(1)}
//                 disabled={pagination.currentPage === 1 || isDisabled}
//                 aria-label="First page"
//               >
//                 <Icon icon="ep:d-arrow-left" style={iconStyle} />
//               </button>
//             </li>
//             {/* Prev */}
//             <li
//               className={`page-item ${
//                 pagination.currentPage === 1 || isDisabled
//                   ? "disabled"
//                   : ""
//               }`}
//             >
//               <button
//                 className="page-link"
//                 style={buttonStyle("#e2533dff", "white")}
//                 onClick={() => handlePageChangeWithDelay(pagination.currentPage - 1)}
//                 disabled={pagination.currentPage === 1 || isDisabled}
//                 aria-label="Previous page"
//               >
//                 <Icon icon="ep:arrow-left" style={iconStyle} />
//               </button>
//             </li>
//             {/* Pages */}
//             {pageNumbers.map((page, index) => (
//               <li
//                 key={index}
//                 className={`page-item ${
//                   pagination.currentPage === page ? "active" : ""
//                 } ${isDisabled ? "disabled" : ""}`}
//               >
//                 <button
//                   className="page-link"
//                   style={buttonStyle(
//                     pagination.currentPage === page ? "#E63C23" : "#F5F5F5",
//                     pagination.currentPage === page ? "white" : "#E63C23"
//                   )}
//                   onClick={() => handlePageChangeWithDelay(page)}
//                   disabled={isDisabled}
//                   aria-label={`Go to page ${page}`}
//                 >
//                   {page}
//                 </button>
//               </li>
//             ))}
//             {/* Next */}
//             <li
//               className={`page-item ${
//                 pagination.currentPage === totalPages || isDisabled
//                   ? "disabled"
//                   : ""
//               }`}
//             >
//               <button
//                 className="page-link"
//                 style={buttonStyle("#e2533dff", "white")}
//                 onClick={() => handlePageChangeWithDelay(pagination.currentPage + 1)}
//                 disabled={
//                   pagination.currentPage === totalPages || isDisabled
//                 }
//                 aria-label="Next page"
//               >
//                 <Icon icon="ep:arrow-right" style={iconStyle} />
//               </button>
//             </li>
//             {/* Last */}
//             <li
//               className={`page-item ${
//                 pagination.currentPage === totalPages || isDisabled
//                   ? "disabled"
//                   : ""
//               }`}
//             >
//               <button
//                 className="page-link"
//                 style={buttonStyle("#e2533dff", "white")}
//                 onClick={() => handlePageChangeWithDelay(totalPages)}
//                 disabled={
//                   pagination.currentPage === totalPages || isDisabled
//                 }
//                 aria-label="Last page"
//               >
//                 <Icon icon="ep:d-arrow-right" style={iconStyle} />
//               </button>
//             </li>
//           </ul>
//         </nav>
//       </div>
//     </>
//   );
// };

// // Button style
// const buttonStyle = (bg, color) => ({
//   backgroundColor: bg,
//   color: color,
//   border: "none",
//   borderRadius: "50%",
//   width: "36px",
//   height: "36px",
//   display: "flex",
//   alignItems: "center",
//   justifyContent: "center",
//   transition: "background-color 0.2s, transform 0.1s",
//   boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
//   cursor: "pointer",
// });

// const iconStyle = { fontSize: "20px", color: "white" };

// export default PaginationControls;